import type { ReactNode } from 'react'
import { auroraStore, useAuroraStore } from '../store/auroraStore'
import { activeChild } from '../store/selectors'
import { SaplingMark } from './ui/SaplingMark'

const NAV = [
  { key: 'thread', label: 'Thread', icon: ChatIcon },
  { key: 'timeline', label: 'Timeline', icon: TimelineIcon },
  { key: 'playroom', label: 'Playroom', icon: PlayroomIcon },
  { key: 'story', label: 'Progress', icon: ChartIcon },
  { key: 'library', label: 'Library', icon: LeafIcon },
] as const

export function AppShell({ children }: { children: ReactNode }) {
  const view = useAuroraStore((s: any) => s.ui.view)
  const child = useAuroraStore(activeChild)
  const online = useAuroraStore((s: any) => s.ui.online)
  const setView = (v: string) => (auroraStore.getState() as any).setView(v)

  const childButton = child && (
    <button
      className="shell-child"
      onClick={() => setView('child')}
      aria-current={view === 'child' ? 'page' : undefined}
    >
      <span className="shell-child-avatar" aria-hidden="true">{child.name.slice(0, 1)}</span>
      <span className="shell-child-name">{child.name}</span>
    </button>
  )
  const offline = !online && (
    <span className="shell-offline" title="Offline. Your work is saved and will sync">
      <CloudOffIcon />
      Offline
    </span>
  )

  return (
    <div className="shell">
      <header className="shell-top">
        <div className="shell-brand">
          <SaplingMark size={22} />
          <span className="shell-brand-name">Sapling</span>
        </div>
        {childButton}
        <div className="shell-top-right">{offline}</div>
      </header>

      <main className="shell-main">{children}</main>

      <nav className="shell-nav" aria-label="Main">
        <div className="shell-nav-brand">
          <SaplingMark size={26} />
          <span>Sapling</span>
        </div>
        {NAV.map(({ key, label, icon: Icon }) => {
          // A game's page is a room in the Playroom.
          const active = view === key || (key === 'playroom' && view === 'game')
          const badge = undefined as number | undefined
          return (
            <button
              key={key}
              className={`shell-nav-item${active ? ' is-active' : ''}`}
              onClick={() => setView(key)}
              aria-current={active ? 'page' : undefined}
            >
              <span className="shell-nav-icon">
                <Icon />
                {badge !== undefined && <span className="shell-nav-badge">{badge}</span>}
              </span>
              <span className="shell-nav-label">{label}</span>
            </button>
          )
        })}
        <div className="shell-nav-foot">
          {childButton}
          {offline}
        </div>
      </nav>
    </div>
  )
}

/* ── marks & icons ──────────────────────────────────────────
   Inline so the shell has no icon-library dependency and the stroke
   inherits currentColor cleanly in both themes. */


const S = {
  width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none',
  stroke: 'currentColor', strokeWidth: 1.75,
  strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const,
  'aria-hidden': true,
}

function ChartIcon() {
  return (
    <svg {...S}>
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
      <path d="M7 15l3.5-4.5 3 3L20 6" />
    </svg>
  )
}

/** A toy block with a ball resting against it: where she plays. */
function PlayroomIcon() {
  return (
    <svg {...S}>
      <rect x="3.5" y="9.5" width="10" height="10" rx="2" />
      <path d="M7 13.2l1.5-1.2 1.5 1.2M7.2 16.5h3.6" />
      <circle cx="17.5" cy="15.5" r="4" />
      <path d="M14.3 13.5c1.8.7 4.6.7 6.4 0" />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg {...S}>
      <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.8-.9L3 21l1.9-5A8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4z" />
    </svg>
  )
}

function LeafIcon() {
  return (
    <svg {...S}>
      <path d="M11 20A7 7 0 0 1 4 13c0-6 7-10 16-10 0 9-4 16-9 17z" />
      <path d="M6.5 18.5C9 15 13 11.5 17 9.5" />
    </svg>
  )
}

function CloudOffIcon() {
  return (
    <svg {...S} width="14" height="14">
      <path d="M3 3l18 18" />
      <path d="M7.7 7.7A5 5 0 0 0 7 17h10a4 4 0 0 0 1.6-7.7" />
    </svg>
  )
}

function TimelineIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M6 4v16" />
      <circle cx="6" cy="8" r="2" fill="currentColor" stroke="none" />
      <circle cx="6" cy="16" r="2" fill="currentColor" stroke="none" />
      <path d="M11 8h8M11 16h6" />
    </svg>
  )
}
